import dayjs from 'dayjs'
import {
  AggregationOp,
  type TimeSeriesDataPoint,
  type TimeSeriesResponse,
  useTimeSeriesService,
} from 'features/timeSeries/timeSeriesService'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChartTimeUnit } from 'types'

const DEFAULT_LOOKBACK_HOURS = 24
const CACHE_MAX_AGE_MS = 5 * 60 * 1000
const DEBOUNCE_MS = 350
const EMPTY_SENSOR_IDS_KEY = '[]'

interface CachedAvailability {
  checkedAt: number
  hasRecentData: boolean
}

interface UseSensorDataAvailabilityOptions {
  /**
   * How far back to look (in hours) when deciding whether a sensor has any
   * recent data. Defaults to 24h to preserve the original behavior. Pass a
   * larger value (e.g. 168 = 7 days) when the consumer wants to keep pens
   * that the underlying chart will still render.
   */
  lookbackHours?: number
}

const hasCountedData = (points: TimeSeriesDataPoint[] | undefined): boolean =>
  points?.some((point) => typeof point.value === 'number' && point.value > 0) ??
  false

const getUniqueSensorIds = (sensorIds: string[]): string[] =>
  [...new Set(sensorIds)].filter(Boolean).sort()

export const useSensorDataAvailability = (
  sensorIds: string[],
  options?: UseSensorDataAvailabilityOptions
) => {
  const lookbackHours = options?.lookbackHours ?? DEFAULT_LOOKBACK_HOURS
  const { fetchTimeSeriesDataStream } = useTimeSeriesService()
  // Cache is keyed on sensorId+lookback so two consumers with different
  // windows (e.g. 24h vs 7d) don't poison each other's results.
  const cacheRef = useRef<Map<string, CachedAvailability>>(new Map())
  const [hasRecentDataBySensorId, setHasRecentDataBySensorId] = useState<
    Record<string, boolean | undefined>
  >({})
  const [isLoading, setIsLoading] = useState(false)

  const sensorIdsKey = useMemo(
    () => JSON.stringify(getUniqueSensorIds(sensorIds)),
    [sensorIds]
  )
  const [debouncedSensorIdsKey, setDebouncedSensorIdsKey] =
    useState(sensorIdsKey)
  const uniqueSensorIds = useMemo(
    () => JSON.parse(debouncedSensorIdsKey) as string[],
    [debouncedSensorIdsKey]
  )

  useEffect(() => {
    if (sensorIdsKey === debouncedSensorIdsKey) {
      return
    }
    // Empty input clears immediately so consumers see the reset right away.
    if (sensorIdsKey === EMPTY_SENSOR_IDS_KEY) {
      setDebouncedSensorIdsKey(sensorIdsKey)
      return
    }
    setIsLoading(true)
    const handle = setTimeout(() => {
      setDebouncedSensorIdsKey(sensorIdsKey)
    }, DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [sensorIdsKey, debouncedSensorIdsKey])

  useEffect(() => {
    if (uniqueSensorIds.length === 0) {
      setHasRecentDataBySensorId({})
      setIsLoading(false)
      return
    }

    const now = Date.now()
    const cachedResults: Record<string, boolean | undefined> = {}
    const idsToFetch: string[] = []

    uniqueSensorIds.forEach((sensorId) => {
      const cachedAvailability = cacheRef.current.get(
        `${sensorId}|${lookbackHours}`
      )
      if (
        cachedAvailability &&
        now - cachedAvailability.checkedAt < CACHE_MAX_AGE_MS
      ) {
        cachedResults[sensorId] = cachedAvailability.hasRecentData
      } else {
        idsToFetch.push(sensorId)
      }
    })

    setHasRecentDataBySensorId((prev) =>
      Object.fromEntries(
        uniqueSensorIds.map((sensorId) => [
          sensorId,
          cachedResults[sensorId] ?? prev[sensorId],
        ])
      )
    )

    if (idsToFetch.length === 0) {
      setIsLoading(false)
      return
    }

    const abortController = new AbortController()
    const end = dayjs()
    const start = end.subtract(lookbackHours, 'hours')
    const idsToFetchSet = new Set(idsToFetch)
    setIsLoading(true)

    const mergeFetchedResults = (
      response: TimeSeriesResponse,
      sensorIds: string[]
    ) => {
      const checkedAt = Date.now()
      const fetchedResults = Object.fromEntries(
        sensorIds.map((sensorId) => {
          const hasRecentData = hasCountedData(response[sensorId])
          cacheRef.current.set(`${sensorId}|${lookbackHours}`, {
            checkedAt,
            hasRecentData,
          })
          return [sensorId, hasRecentData]
        })
      )

      if (Object.keys(fetchedResults).length === 0) {
        return
      }

      setHasRecentDataBySensorId((prev) =>
        Object.fromEntries(
          uniqueSensorIds.map((sensorId) => [
            sensorId,
            fetchedResults[sensorId] ??
              cachedResults[sensorId] ??
              prev[sensorId],
          ])
        )
      )
    }

    const surveySensors = async () => {
      try {
        const response = await fetchTimeSeriesDataStream(
          {
            entry_ids: idsToFetch,
            start_datetime: start.toISOString(),
            end_datetime: end.toISOString(),
            interval: lookbackHours,
            interval_unit: ChartTimeUnit.HOURS,
            aggregation_op: AggregationOp.COUNT,
            interpolate: false,
          },
          {
            signal: abortController.signal,
            onChunk: (chunk) => {
              if (abortController.signal.aborted) {
                return
              }

              const chunkSensorIds = Object.keys(chunk).filter((sensorId) =>
                idsToFetchSet.has(sensorId)
              )
              mergeFetchedResults(chunk, chunkSensorIds)
            },
          }
        )

        if (abortController.signal.aborted) {
          return
        }

        mergeFetchedResults(response, idsToFetch)
      } catch (error) {
        if (abortController.signal.aborted) {
          return
        }

        console.error('Failed to survey sensor data availability:', error)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false)
        }
      }
    }

    surveySensors()

    return () => abortController.abort()
  }, [
    fetchTimeSeriesDataStream,
    debouncedSensorIdsKey,
    uniqueSensorIds,
    lookbackHours,
  ])

  return {
    hasRecentDataBySensorId,
    isLoading,
  }
}
